import { useCallback, useId, useState } from 'react'
import { Link } from 'react-router-dom'
import {
  BarChart3,
  Bot,
  BellRing,
  Boxes,
  ChevronDown,
  ClipboardList,
  Home,
  Package,
  Plus,
  Settings,
  User,
  UsersRound,
  Warehouse,
  type LucideIcon,
} from 'lucide-react'
import { Button } from '@/components/ui/button'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import {
  SidebarGroup,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
} from '@/components/ui/sidebar'
import { cn } from '@/lib/utils'
import { useOnboarding } from '@/src/components/onboarding/Onboarding'
import {
  groupHolding,
  isMenuItemActive,
  parseCollapsed,
  tourName,
  type CreateItem,
  type MenuGroup,
  type MenuGroupId,
  type MenuItem,
} from '@/src/lib/navigation'

const ICONS: Record<string, LucideIcon> = {
  '/': Home,
  '/attention': BellRing,
  '/assistant': Bot,
  '/inquiries': ClipboardList,
  '/consignments': Package,
  '/parcels': Boxes,
  '/warehouse': Warehouse,
  '/customers': User,
  '/settings/users': UsersRound,
  '/settings': Settings,
  '/docs': BarChart3,
}

const KEY = 'helm:sidebar-collapsed:'

/** Which groups this person keeps collapsed, remembered in this browser. */
function useCollapsedGroups(userId: string) {
  const [collapsed, setCollapsed] = useState<MenuGroupId[]>(() => {
    try {
      return parseCollapsed(window.localStorage.getItem(KEY + userId))
    } catch {
      // Private windows and blocked storage throw: every group starts open.
      return []
    }
  })
  const toggle = useCallback(
    (id: MenuGroupId) =>
      setCollapsed((current) => {
        const next = current.includes(id)
          ? current.filter((group) => group !== id)
          : [...current, id]
        try {
          window.localStorage.setItem(KEY + userId, JSON.stringify(next))
        } catch {
          // Not remembered; the menu still works.
        }
        return next
      }),
    [userId],
  )
  return { collapsed, toggle }
}

function MenuLinks({ items, pathname }: { items: readonly MenuItem[]; pathname: string }) {
  return (
    <SidebarMenu>
      {items.map((item) => {
        const Icon = ICONS[item.url] ?? Home
        return (
          <SidebarMenuItem key={item.url}>
            <SidebarMenuButton asChild isActive={isMenuItemActive(pathname, item)}>
              <Link to={item.url} data-tour={tourName(item.title)}>
                <Icon className="h-4 w-4" />
                <span>{item.title}</span>
              </Link>
            </SidebarMenuButton>
          </SidebarMenuItem>
        )
      })}
    </SidebarMenu>
  )
}

function NavGroup({
  group,
  pathname,
  open,
  locked,
  onToggle,
}: {
  group: MenuGroup
  pathname: string
  open: boolean
  /** Holds the page someone is on, or what a tour is pointing at: stays open. */
  locked: boolean
  onToggle: () => void
}) {
  const contentId = useId()
  return (
    <SidebarGroup data-tour={`nav-group-${group.id}`} className="py-1">
      <SidebarGroupLabel asChild>
        <button
          type="button"
          onClick={onToggle}
          disabled={locked}
          aria-expanded={open}
          aria-controls={contentId}
          className="w-full justify-between uppercase tracking-wide hover:text-sidebar-foreground disabled:cursor-default"
        >
          {group.label}
          {!locked && (
            <ChevronDown
              aria-hidden
              className={cn(
                'transition-transform motion-reduce:transition-none',
                !open && '-rotate-90',
              )}
            />
          )}
        </button>
      </SidebarGroupLabel>
      {open && (
        <SidebarGroupContent id={contentId}>
          <MenuLinks items={group.items} pathname={pathname} />
        </SidebarGroupContent>
      )}
    </SidebarGroup>
  )
}

/** One "New" button for everything that can be created. */
function NewMenu({ items }: { items: readonly CreateItem[] }) {
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="outline" className="w-full justify-start" data-tour="nav-new">
          <Plus />
          New
          <ChevronDown className="ml-auto" aria-hidden />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent className="w-(--radix-dropdown-menu-trigger-width)">
        {items.map((item) => (
          <DropdownMenuItem key={item.url} asChild>
            <Link to={item.url} data-tour={tourName(item.title)}>
              {item.label}
            </Link>
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  )
}

/**
 * The sidebar's menu: a New button, then the groups. Each group folds away
 * and stays folded for this person; the one holding the current page, or
 * what a running tour points at, is always open. The warehouse team's short
 * menu is a plain list.
 */
export function SidebarNav({
  userId,
  pathname,
  navigation,
}: {
  userId: string
  pathname: string
  navigation: { flat: boolean; groups: MenuGroup[]; create: readonly CreateItem[] }
}) {
  const { collapsed, toggle } = useCollapsedGroups(userId)
  const { activeTarget } = useOnboarding()
  const current = groupHolding(pathname, navigation.groups)

  return (
    <>
      {navigation.create.length > 0 && (
        <SidebarGroup className="pt-0">
          <NewMenu items={navigation.create} />
        </SidebarGroup>
      )}
      {navigation.flat ? (
        <SidebarGroup>
          <SidebarGroupContent>
            <MenuLinks
              items={navigation.groups.flatMap((group) => group.items)}
              pathname={pathname}
            />
          </SidebarGroupContent>
        </SidebarGroup>
      ) : (
        navigation.groups.map((group) => {
          const locked =
            group.id === current ||
            group.items.some((item) => tourName(item.title) === activeTarget)
          return (
            <NavGroup
              key={group.id}
              group={group}
              pathname={pathname}
              open={locked || !collapsed.includes(group.id)}
              locked={locked}
              onToggle={() => toggle(group.id)}
            />
          )
        })
      )}
    </>
  )
}
